(function () {
  'use strict';

  const root = document.querySelector('[data-isee-checkout]');
  if (!root) return;

  const sectionId = root.dataset.sectionId;
  const VIP_STORAGE_KEY = 'isee-checkout-vip';
  const SALE_STORAGE_KEY = 'isee-checkout-sale';
  const SALE_CODE = String(root.dataset.saleCode || 'SALE').toUpperCase();
  const SALE_CENTS = parseInt(root.dataset.saleCents || '300', 10) || 300;

  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, root.dataset.moneyFormat || window.theme?.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  };

  const getCartTotalCents = () => {
    const el = root.querySelector('[data-checkout-cart-total-cents]');
    return parseInt(el?.textContent || el?.dataset.checkoutCartTotalCents || '0', 10) || 0;
  };

  const getVipFeeCents = () => {
    const vip = root.querySelector('[data-checkout-vip]');
    return parseInt(vip?.dataset.vipFeeCents || '0', 10) || 0;
  };

  const isVipSelected = () => Boolean(root.querySelector('[data-checkout-vip]')?.checked);

  const isSaleApplied = () => {
    try {
      return sessionStorage.getItem(SALE_STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  const setSaleApplied = (applied) => {
    try {
      sessionStorage.setItem(SALE_STORAGE_KEY, applied ? '1' : '0');
    } catch (e) {
      /* ignore */
    }
  };

  const updateTotals = () => {
    const vipSelected = isVipSelected();
    const saleApplied = isSaleApplied();
    const subtotal = getCartTotalCents();
    const saleCents = saleApplied ? SALE_CENTS : 0;
    const vipFee = vipSelected ? getVipFeeCents() : 0;
    const grandTotal = Math.max(0, subtotal - saleCents + vipFee);

    const vipRow = root.querySelector('[data-checkout-vip-row]');
    if (vipRow) {
      vipRow.hidden = !vipSelected;
      vipRow.classList.toggle('is-hidden', !vipSelected);
    }

    const discountRow = root.querySelector('[data-checkout-discount-row]');
    if (discountRow) {
      discountRow.hidden = !saleApplied;
      discountRow.classList.toggle('is-hidden', !saleApplied);
      const amountEl = discountRow.querySelector('[data-checkout-discount]');
      const codeEl = discountRow.querySelector('[data-checkout-discount-code]');
      if (amountEl) amountEl.textContent = saleApplied ? `-${formatMoney(SALE_CENTS)}` : '';
      if (codeEl) codeEl.textContent = saleApplied ? `(${SALE_CODE})` : '';
    }

    const appliedEl = root.querySelector('[data-checkout-applied-codes]');
    if (appliedEl) {
      appliedEl.hidden = !saleApplied;
      appliedEl.textContent = saleApplied ? `Applied: ${SALE_CODE}` : '';
    }

    const subtotalEl = root.querySelector('[data-checkout-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);

    const grandEl = root.querySelector('[data-checkout-grand-total]');
    if (grandEl) grandEl.textContent = formatMoney(grandTotal);

    const pointsEl = root.querySelector('[data-checkout-points]');
    if (pointsEl) pointsEl.textContent = String(Math.floor(grandTotal / 100));

    try {
      sessionStorage.setItem(VIP_STORAGE_KEY, vipSelected ? '1' : '0');
    } catch (e) {
      /* ignore */
    }
  };

  const restoreVipSelection = () => {
    const vip = root.querySelector('[data-checkout-vip]');
    if (vip) {
      try {
        vip.checked = sessionStorage.getItem(VIP_STORAGE_KEY) === '1';
      } catch (e) {
        vip.checked = false;
      }
    }
    updateTotals();
  };

  const applySectionHtml = (html) => {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('[data-isee-checkout]');
    if (!next) return false;
    root.innerHTML = next.innerHTML;
    bind();
    return true;
  };

  const refresh = async () => {
    if (!sectionId) {
      window.location.reload();
      return;
    }
    root.classList.add('is-busy');
    try {
      const res = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`);
      const html = await res.text();
      if (!applySectionHtml(html)) {
        window.location.reload();
      }
    } finally {
      root.classList.remove('is-busy');
    }
  };

  const changeQty = async (lineKey, quantity) => {
    const nextQty = Math.max(0, Number(quantity) || 0);
    root.classList.add('is-busy');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: nextQty,
        }),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || 'Could not update quantity');

      // Always re-render section from live cart so line prices / subtotal / grand total stay in sync
      await refresh();
    } catch (e) {
      console.error(e);
      await refresh();
    } finally {
      root.classList.remove('is-busy');
    }
  };

  const showDiscountError = (message) => {
    const err = root.querySelector('[data-checkout-discount-error]');
    if (!err) return;
    err.hidden = false;
    err.textContent = message;
  };

  const hideDiscountError = () => {
    const err = root.querySelector('[data-checkout-discount-error]');
    if (!err) return;
    err.hidden = true;
    err.textContent = '';
  };

  const applyDiscount = async (code) => {
    hideDiscountError();
    const trimmed = String(code || '').trim();
    if (!trimmed) {
      showDiscountError('Please enter a discount code');
      return;
    }

    if (trimmed.toUpperCase() !== SALE_CODE) {
      showDiscountError('Invalid discount code');
      return;
    }

    setSaleApplied(true);
    updateTotals();

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: SALE_CODE,
          attributes: { discount_code: SALE_CODE },
        }),
      });
    } catch (e) {
      console.warn(e);
    }
  };

  const getField = (sel) => root.querySelector(sel)?.value?.trim() || '';

  const getProvinceValue = () => {
    const wrap = root.querySelector('[data-checkout-province-wrap]');
    if (!wrap) return '';
    const select = wrap.querySelector('[data-checkout-province-select]');
    const input = wrap.querySelector('[data-checkout-province-input]');
    if (select && !select.hidden) return select.value.trim();
    return input?.value.trim() || '';
  };

  const PROVINCE_LABELS = {
    US: 'State',
    FR: 'Region',
  };

  const FALLBACK_PROVINCES = {
    US: [
      { name: 'Alabama', code: 'AL' },
      { name: 'Alaska', code: 'AK' },
      { name: 'Arizona', code: 'AZ' },
      { name: 'Arkansas', code: 'AR' },
      { name: 'California', code: 'CA' },
      { name: 'Colorado', code: 'CO' },
      { name: 'Connecticut', code: 'CT' },
      { name: 'Delaware', code: 'DE' },
      { name: 'District of Columbia', code: 'DC' },
      { name: 'Florida', code: 'FL' },
      { name: 'Georgia', code: 'GA' },
      { name: 'Hawaii', code: 'HI' },
      { name: 'Idaho', code: 'ID' },
      { name: 'Illinois', code: 'IL' },
      { name: 'Indiana', code: 'IN' },
      { name: 'Iowa', code: 'IA' },
      { name: 'Kansas', code: 'KS' },
      { name: 'Kentucky', code: 'KY' },
      { name: 'Louisiana', code: 'LA' },
      { name: 'Maine', code: 'ME' },
      { name: 'Maryland', code: 'MD' },
      { name: 'Massachusetts', code: 'MA' },
      { name: 'Michigan', code: 'MI' },
      { name: 'Minnesota', code: 'MN' },
      { name: 'Mississippi', code: 'MS' },
      { name: 'Missouri', code: 'MO' },
      { name: 'Montana', code: 'MT' },
      { name: 'Nebraska', code: 'NE' },
      { name: 'Nevada', code: 'NV' },
      { name: 'New Hampshire', code: 'NH' },
      { name: 'New Jersey', code: 'NJ' },
      { name: 'New Mexico', code: 'NM' },
      { name: 'New York', code: 'NY' },
      { name: 'North Carolina', code: 'NC' },
      { name: 'North Dakota', code: 'ND' },
      { name: 'Ohio', code: 'OH' },
      { name: 'Oklahoma', code: 'OK' },
      { name: 'Oregon', code: 'OR' },
      { name: 'Pennsylvania', code: 'PA' },
      { name: 'Rhode Island', code: 'RI' },
      { name: 'South Carolina', code: 'SC' },
      { name: 'South Dakota', code: 'SD' },
      { name: 'Tennessee', code: 'TN' },
      { name: 'Texas', code: 'TX' },
      { name: 'Utah', code: 'UT' },
      { name: 'Vermont', code: 'VT' },
      { name: 'Virginia', code: 'VA' },
      { name: 'Washington', code: 'WA' },
      { name: 'West Virginia', code: 'WV' },
      { name: 'Wisconsin', code: 'WI' },
      { name: 'Wyoming', code: 'WY' },
    ],
    FR: [
      { name: 'Auvergne-Rhône-Alpes', code: 'ARA' },
      { name: 'Bourgogne-Franche-Comté', code: 'BFC' },
      { name: 'Bretagne', code: 'BRE' },
      { name: 'Centre-Val de Loire', code: 'CVL' },
      { name: 'Corse', code: 'COR' },
      { name: 'Grand Est', code: 'GES' },
      { name: 'Hauts-de-France', code: 'HDF' },
      { name: 'Île-de-France', code: 'IDF' },
      { name: 'Normandie', code: 'NOR' },
      { name: 'Nouvelle-Aquitaine', code: 'NAQ' },
      { name: 'Occitanie', code: 'OCC' },
      { name: 'Pays de la Loire', code: 'PDL' },
      { name: "Provence-Alpes-Côte d'Azur", code: 'PAC' },
      { name: 'Guadeloupe', code: 'GUA' },
      { name: 'Guyane', code: 'GUY' },
      { name: 'La Réunion', code: 'LRE' },
      { name: 'Martinique', code: 'MTQ' },
      { name: 'Mayotte', code: 'MAY' },
    ],
  };

  const getProvinceData = () => {
    const el = root.querySelector('[data-checkout-provinces]');
    let fromShopify = {};
    if (el) {
      try {
        fromShopify = JSON.parse(el.textContent || '{}');
      } catch (e) {
        fromShopify = {};
      }
    }
    return {
      US: Array.isArray(fromShopify.US) && fromShopify.US.length ? fromShopify.US : FALLBACK_PROVINCES.US,
      FR: Array.isArray(fromShopify.FR) && fromShopify.FR.length ? fromShopify.FR : FALLBACK_PROVINCES.FR,
    };
  };

  const updateProvinceField = (countryCode, preserveValue = false) => {
    const wrap = root.querySelector('[data-checkout-province-wrap]');
    if (!wrap) return;

    const select = wrap.querySelector('[data-checkout-province-select]');
    const input = wrap.querySelector('[data-checkout-province-input]');
    const label = wrap.querySelector('[data-checkout-province-label]');
    const provinces = getProvinceData()[countryCode] || [];
    const labelText = PROVINCE_LABELS[countryCode] || 'State/Province';
    const previous = preserveValue ? getProvinceValue() : '';

    if (label) label.textContent = labelText;

    if (provinces.length && select) {
      const placeholder = `Select ${labelText.toLowerCase()}`;
      select.innerHTML = `<option value="">${placeholder}</option>`;
      provinces.forEach((province) => {
        const option = document.createElement('option');
        option.value = province.code || province.name;
        option.textContent = province.name;
        if (previous && (option.value === previous || province.name === previous)) {
          option.selected = true;
        }
        select.appendChild(option);
      });
      select.hidden = false;
      select.removeAttribute('hidden');
      select.disabled = false;
      select.style.display = '';
      if (input) {
        input.hidden = true;
        input.disabled = true;
        input.style.display = 'none';
        input.value = '';
      }
      return;
    }

    if (input) {
      input.hidden = false;
      input.removeAttribute('hidden');
      input.disabled = false;
      input.style.display = '';
      input.placeholder = labelText;
      input.value = previous;
    }
    if (select) {
      select.hidden = true;
      select.disabled = true;
      select.style.display = 'none';
      select.innerHTML = `<option value="">${labelText}</option>`;
    }
  };

  const initProvinceField = () => {
    const country = root.querySelector('[data-checkout-country]');
    if (!country) return;

    country.addEventListener('change', () => {
      updateProvinceField(country.value, false);
    });

    updateProvinceField(country.value || 'US', false);
  };

  const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

  const formatCardNumber = (value) => {
    const digits = digitsOnly(value).slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  const formatCardExpiry = (value) => {
    const digits = digitsOnly(value).slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  const validateCardFields = () => {
    const method = root.querySelector('[data-payment-method]:checked')?.value || 'card';
    if (method !== 'card') return true;

    const cardInput = root.querySelector('[data-card-number]');
    const expInput = root.querySelector('[data-card-exp]');
    const cvcInput = root.querySelector('[data-card-cvc]');
    const cardDigits = digitsOnly(cardInput?.value);

    if (cardDigits.length < 13) {
      cardInput?.focus();
      alert('Please enter a valid card number');
      return false;
    }

    const expMatch = getField('[data-card-exp]').match(/^(\d{2})\/(\d{2})$/);
    if (!expMatch) {
      expInput?.focus();
      alert('Please enter expiry date as MM/YY');
      return false;
    }

    const month = Number(expMatch[1]);
    const year = Number(`20${expMatch[2]}`);
    if (month < 1 || month > 12) {
      expInput?.focus();
      alert('Please enter a valid expiry date');
      return false;
    }

    const now = new Date();
    const expiry = new Date(year, month, 0, 23, 59, 59);
    if (expiry < now) {
      expInput?.focus();
      alert('This card has expired');
      return false;
    }

    const cvcDigits = digitsOnly(cvcInput?.value);
    if (cvcDigits.length < 3) {
      cvcInput?.focus();
      alert('Please enter a valid security code');
      return false;
    }

    return true;
  };

  const initCardFields = () => {
    const cardInput = root.querySelector('[data-card-number]');
    const expInput = root.querySelector('[data-card-exp]');
    const cvcInput = root.querySelector('[data-card-cvc]');
    const saveCard = root.querySelector('[data-save-card]');

    cardInput?.addEventListener('input', () => {
      cardInput.value = formatCardNumber(cardInput.value);
    });

    expInput?.addEventListener('input', () => {
      expInput.value = formatCardExpiry(expInput.value);
    });

    cvcInput?.addEventListener('input', () => {
      cvcInput.value = digitsOnly(cvcInput.value).slice(0, 4);
    });

    try {
      const saved = sessionStorage.getItem('isee-checkout-save-card');
      if (saved === '1' && saveCard) saveCard.checked = true;
    } catch (e) {
      /* ignore */
    }

    saveCard?.addEventListener('change', () => {
      try {
        sessionStorage.setItem('isee-checkout-save-card', saveCard.checked ? '1' : '0');
      } catch (e) {
        /* ignore */
      }
    });
  };

  const DUPLICATE_WALLET_SELECTORS = [
    'apple-pay-button',
    'button[aria-label*="Apple Pay" i]',
    '[data-testid*="ApplePay" i]',
    '[data-testid*="apple-pay" i]',
    'button[aria-label*="Shop Pay" i]',
    '[data-testid*="ShopPay" i]',
    '[data-testid*="shop-pay" i]',
  ];

  const hideDuplicateExpressWallets = () => {
    const container = root.querySelector('.isee-checkout__express-buttons');
    const custom = root.querySelector('[data-express-apple-pay]');
    if (!container || !custom) return;

    DUPLICATE_WALLET_SELECTORS.forEach((sel) => {
      container.querySelectorAll(sel).forEach((node) => {
        if (node.closest('[data-express-apple-pay]')) return;
        const host = node.closest('.shopify-cleanslate, .dynamic-checkout__content, .shopify-payment-button') || node;
        host.classList.add('isee-checkout__express-native-wallet');
      });
    });
  };

  const proceedToCheckout = async (paymentMethod) => {
    const email = getField('[data-checkout-email]');
    root.classList.add('is-busy');
    try {
      const attributes = {
        payment_method: paymentMethod,
        vip_membership: isVipSelected() ? 'yes' : 'no',
      };
      if (email) attributes.checkout_email = email;
      const phone = getField('[data-checkout-phone]');
      if (phone) attributes.checkout_phone = phone;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          note: email ? `Checkout email: ${email}` : undefined,
          attributes,
        }),
      });
    } catch (e) {
      console.warn(e);
    }

    window.location.assign(buildCheckoutUrl());
  };

  const expressApplePay = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await proceedToCheckout('apple_pay');
  };

  const initExpressApplePay = () => {
    hideDuplicateExpressWallets();
    window.setTimeout(hideDuplicateExpressWallets, 600);
    window.setTimeout(hideDuplicateExpressWallets, 1500);
  };

  let expressApplePayBound = false;

  const bindExpressApplePay = () => {
    if (expressApplePayBound) return;
    root.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-express-apple-pay]');
      if (!btn || !root.contains(btn)) return;
      expressApplePay(event);
    });
    expressApplePayBound = true;
  };

  const buildCheckoutUrl = () => {
    const base = root.dataset.checkoutAction || '/checkout';
    const params = new URLSearchParams();
    const email = getField('[data-checkout-email]');
    const map = [
      ['checkout[email]', email],
      ['checkout[shipping_address][first_name]', getField('[data-checkout-first-name]')],
      ['checkout[shipping_address][last_name]', getField('[data-checkout-last-name]')],
      ['checkout[shipping_address][address1]', getField('[data-checkout-address1]')],
      ['checkout[shipping_address][city]', getField('[data-checkout-city]')],
      ['checkout[shipping_address][zip]', getField('[data-checkout-zip]')],
      ['checkout[shipping_address][country]', getField('[data-checkout-country]')],
      ['checkout[shipping_address][province]', getProvinceValue()],
      ['checkout[shipping_address][phone]', getField('[data-checkout-phone]')],
    ];
    map.forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  };

  const payNow = async () => {
    const email = getField('[data-checkout-email]');
    const required = [
      ['[data-checkout-email]', 'Email'],
      ['[data-checkout-first-name]', 'First name'],
      ['[data-checkout-last-name]', 'Last name'],
      ['[data-checkout-address1]', 'Address'],
      ['[data-checkout-city]', 'City'],
      ['[data-checkout-zip]', 'Postcode'],
    ];
    for (const [sel, label] of required) {
      const el = root.querySelector(sel);
      if (!el?.value?.trim()) {
        el?.focus();
        alert(`Please enter ${label}`);
        return;
      }
    }

    if (!validateCardFields()) return;

    const cardDigits = digitsOnly(getField('[data-card-number]'));
    const cardLast4 = cardDigits.slice(-4);
    const saveCard = root.querySelector('[data-save-card]')?.checked ? 'yes' : 'no';

    root.classList.add('is-busy');
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          note: `Checkout email: ${email}`,
          attributes: {
            checkout_email: email,
            checkout_phone: getField('[data-checkout-phone]'),
            vip_membership: isVipSelected() ? 'yes' : 'no',
            payment_method: 'card',
            card_last4: cardLast4,
            save_card: saveCard,
            discount_code: isSaleApplied() ? SALE_CODE : '',
          },
        }),
      });
    } catch (e) {
      console.warn(e);
    }

    window.location.href = buildCheckoutUrl();
  };

  const bind = () => {
    root.querySelectorAll('[data-qty-change]').forEach((btn) => {
      btn.addEventListener('click', () => {
        changeQty(btn.dataset.lineKey, parseInt(btn.dataset.quantity, 10));
      });
    });

    root.querySelectorAll('[data-qty-input]').forEach((input) => {
      input.addEventListener('change', () => {
        changeQty(input.dataset.lineKey, parseInt(input.value, 10) || 1);
      });
    });

    root.querySelectorAll('[data-apply-code]').forEach((btn) => {
      btn.addEventListener('click', () => applyDiscount(btn.dataset.applyCode));
    });

    root.querySelector('[data-checkout-discount-apply]')?.addEventListener('click', () => {
      const code = root.querySelector('[data-checkout-discount-input]')?.value?.trim();
      applyDiscount(code);
    });

    root.querySelector('[data-checkout-discount-input]')?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        applyDiscount(event.currentTarget.value);
      }
    });

    root.querySelector('[data-checkout-pay-now]')?.addEventListener('click', payNow);

    root.querySelector('[data-checkout-save-address]')?.addEventListener('click', () => {
      const btn = root.querySelector('[data-checkout-save-address]');
      if (btn) {
        const orig = btn.textContent;
        btn.textContent = 'Saved ✓';
        setTimeout(() => {
          btn.textContent = orig;
        }, 1500);
      }
    });

    root.querySelectorAll('[data-payment-method]').forEach((radio) => {
      radio.addEventListener('change', () => {
        root.querySelectorAll('.isee-checkout__pay-option').forEach((el) => el.classList.remove('is-active'));
        radio.closest('.isee-checkout__pay-option')?.classList.add('is-active');
      });
    });

    root.querySelectorAll('[data-collapse-target]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.collapseTarget;
        const panel = root.querySelector(`#${id}`);
        if (!panel) return;
        const open = panel.hidden;
        panel.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
      });
    });

    root.querySelector('[data-checkout-vip]')?.addEventListener('change', updateTotals);

    initCardFields();
    initProvinceField();
    initExpressApplePay();
    restoreVipSelection();
  };

  bind();
  bindExpressApplePay();
})();
